import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useLocalize } from '@treams/treams-hooks';
import { StarIcon } from '@treams/treams-icons';
import { useJobTitlesQuery } from '@treams/treams-network';
import { type Option } from '@treams/treams-ui';

type Section = { title: string; options: Option[] };

/**
 * Job titles for the succession Select: key positions (sleutelrollen) first
 * with a star, then the other titles. Shared by the analytics card and the
 * nominate modal.
 */
export function useKeyPositionOptions() {
	const { t } = useTranslation();
	const { localize } = useLocalize();

	const { data, loading } = useJobTitlesQuery({
		variables: { isArchived: false, first: 1000 },
	});

	return useMemo(() => {
		const titles =
			data?.jobTitles.edges.map(({ node }) => ({
				id: node.id,
				title: localize(node.title).text ?? '',
				isKeyPosition: node.isKeyPosition,
			})) ?? [];

		const byTitle = (a: { title: string }, b: { title: string }) => a.title.localeCompare(b.title);

		const keyPositions = titles.filter((title) => title.isKeyPosition).sort(byTitle);
		const others = titles.filter((title) => !title.isKeyPosition).sort(byTitle);

		const toOption = (title: { id: string; title: string; isKeyPosition: boolean }): Option => ({
			value: title.id,
			title: title.title,
			icon: title.isKeyPosition ? <StarIcon /> : undefined,
		});

		const sections: Section[] = [];

		if (keyPositions.length > 0) {
			sections.push({
				title: t('analytics.talentReview.succession.keyPositions'),
				options: keyPositions.map(toOption),
			});
		}

		if (others.length > 0) {
			sections.push({
				title: t('analytics.talentReview.succession.otherPositions'),
				options: others.map(toOption),
			});
		}

		return {
			loading,
			sections,
			hasKeyPositions: keyPositions.length > 0,
			defaultJobTitleID: keyPositions[0]?.id ?? others[0]?.id,
			titleByID: new Map(titles.map((title) => [title.id, title.title])),
		};
	}, [data, loading, localize, t]);
}
